"use client";

import { domAnimation, LazyMotion, m } from "framer-motion";
import { ArrowRight, Clock, Search } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useCallback, useEffect, useRef, useState } from "react";
import { BlogNewsletterForm } from "@/components/blog/newsletter_form";
import { Squares } from "@/components/custom/squares_background";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import type { Blog } from "@/lib/blog-data";
import { setTransition } from "@/lib/stores/blog-transition-store";

const blogDateFormatter = new Intl.DateTimeFormat("en-US", {
	month: "long",
	day: "numeric",
	year: "numeric",
	timeZone: "Asia/Kolkata",
});

interface BlogClientProps {
	initialBlogs: Blog[];
}

interface BlogCardProps {
	blog: Blog;
	index: number;
	cardRefs: Map<string, HTMLElement>;
	onCardClick: (blog: Blog) => void;
}

function BlogCard({ blog, index, cardRefs, onCardClick }: BlogCardProps) {
	return (
		<m.div
			initial={{ opacity: 0, y: 30 }}
			animate={{ opacity: 1, y: 0 }}
			transition={{ delay: index * 0.1, duration: 0.5 }}
		>
			<Link
				href={`/blog/${blog.slug}`}
				onClick={() => onCardClick(blog)}
				className="group h-full block cursor-pointer"
			>
				<article
					className="h-full bg-white rounded-2xl border border-zinc-200 overflow-hidden hover:shadow-xl hover:border-zinc-300 hover:-translate-y-1 transition-[box-shadow,border-color,transform] duration-300 flex flex-col"
					ref={(el) => {
						if (el) cardRefs.set(String(blog.id), el);
					}}
				>
					<div className="aspect-[16/10] w-full overflow-hidden bg-zinc-100 relative">
						<Image
							src={blog.coverImage || "/placeholders/blog-cover.svg"}
							alt={blog.title}
							width={640}
							height={400}
							className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
							loading={index < 3 ? "eager" : "lazy"}
							sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
						/>
						<div className="absolute top-4 left-4 flex flex-wrap gap-2">
							{blog.tags.slice(0, 2).map((tag) => (
								<span
									key={tag}
									className="px-3 py-1 bg-white/90 backdrop-blur-sm rounded-lg text-xs font-bold font-syne uppercase tracking-wide text-zinc-900 border border-zinc-100"
								>
									{tag}
								</span>
							))}
						</div>
					</div>
					<div className="p-8 flex flex-col flex-1">
						<div className="flex items-center text-xs font-medium text-zinc-400 mb-4 space-x-2 font-inter uppercase tracking-wider">
							<span className="flex items-center">
								<Clock className="w-3.5 h-3.5 mr-1" /> {blog.readTime}
							</span>
							<span>&bull;</span>
							<span>{blogDateFormatter.format(new Date(blog.date))}</span>
						</div>

						<h3 className="font-syne text-2xl font-bold uppercase tracking-tight text-zinc-900 mb-3 group-hover:text-primary transition-colors line-clamp-2">
							{blog.title}
						</h3>
						<p className="font-inter text-zinc-500 mb-6 line-clamp-3 leading-relaxed">
							{blog.excerpt}
						</p>

						<div className="mt-auto pt-6 border-t border-zinc-100 flex items-center justify-between">
							<div className="flex items-center">
								<div className="w-8 h-8 rounded-full bg-zinc-100 border border-zinc-200 overflow-hidden mr-3">
									<Image
										src={blog.author.avatar || "/placeholders/avatar.svg"}
										alt={blog.author.name}
										width={32}
										height={32}
										className="w-full h-full object-cover"
									/>
								</div>
								<span className="text-sm font-bold font-syne uppercase tracking-wide text-zinc-900">
									{blog.author.name}
								</span>
							</div>
							<span className="text-primary opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-[opacity,transform] duration-300">
								<ArrowRight className="w-5 h-5" />
							</span>
						</div>
					</div>
				</article>
			</Link>
		</m.div>
	);
}

export default function BlogClient({ initialBlogs }: BlogClientProps) {
	const [blogs] = useState<Blog[]>(initialBlogs);
	const [filteredBlogs, setFilteredBlogs] = useState<Blog[]>(initialBlogs);
	const [searchQuery, setSearchQuery] = useState("");
	const [selectedTag, setSelectedTag] = useState("");
	// Refs for card image elements - keyed by blog id
	const cardRefs = useRef<Map<string, HTMLElement> | null>(null);
	if (cardRefs.current === null) {
		cardRefs.current = new Map<string, HTMLElement>();
	}
	const cardRefsMap = cardRefs.current;

	useEffect(() => {
		let filtered = blogs;

		if (searchQuery) {
			filtered = filtered.filter(
				(blog) =>
					blog.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
					blog.excerpt?.toLowerCase().includes(searchQuery.toLowerCase()),
			);
		}

		if (selectedTag) {
			filtered = filtered.filter((blog) =>
				blog.tags?.some(
					(tag) => tag.toLowerCase() === selectedTag.toLowerCase(),
				),
			);
		}

		setFilteredBlogs(filtered);
	}, [blogs, searchQuery, selectedTag]);

	// Get all unique tags
	const allTags = Array.from(
		new Set(blogs.flatMap((blog) => blog.tags || []).filter(Boolean)),
	);

	const handleCardClick = useCallback(
		(blog: Blog) => {
			const cardEl = cardRefsMap.get(String(blog.id));

			if (cardEl) {
				const cardRect = cardEl.getBoundingClientRect();

				setTransition({
					slug: blog.slug,
					coverImage: blog.coverImage || "/placeholders/blog-cover.svg",
					cardRect,
				});
			}
		},
		[cardRefsMap],
	);

	return (
		<LazyMotion features={domAnimation}>
			<div className="blog-page overflow-x-hidden bg-white min-h-screen">
				{/* Editorial Hero Section - extends behind navbar */}
				<section className="relative h-[60vh] md:min-h-[800px] flex items-center justify-center overflow-hidden bg-zinc-950 text-white -mt-20 pt-20">
					<div className="absolute inset-0 z-0">
						<div className="absolute top-0 right-0 w-[600px] h-[600px] bg-red-600/10 rounded-full blur-[150px]" />
						<div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-blue-600/10 rounded-full blur-[150px]" />
						<Squares
							theme="dark"
							direction="diagonal"
							speed={0.15}
							squareSize={60}
							className="opacity-20"
							hoverFillColor="#ef4444"
						/>
					</div>

					<div className="container mx-auto px-4 relative z-10 text-center">
						<m.div
							initial={{ opacity: 0, y: 30 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.8 }}
						>
							<h1 className="font-syne font-black uppercase leading-[0.85] tracking-tighter mb-12">
								<span className="block text-[15vw] md:text-[10rem] text-zinc-800">
									Our
								</span>
								<span className="block text-[15vw] md:text-[10rem] text-white">
									Journal
								</span>
							</h1>
						</m.div>

						<m.div
							className="max-w-xl mx-auto"
							initial={{ opacity: 0, scale: 0.95 }}
							animate={{ opacity: 1, scale: 1 }}
							transition={{ delay: 0.4, duration: 0.6 }}
						>
							<div className="relative group">
								<Search className="absolute left-6 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-400 group-focus-within:text-red-500 transition-colors" />
								<Input
									type="text"
									placeholder="Search articles..."
									className="h-14 pl-14 pr-6 rounded-full bg-zinc-900 border-2 border-zinc-800 focus:bg-black focus:border-red-600 focus:ring-4 focus:ring-red-600/10 transition-[background-color,border-color,box-shadow] font-inter text-white placeholder:text-zinc-500 shadow-xl"
									value={searchQuery}
									onChange={(e) => setSearchQuery(e.target.value)}
								/>
							</div>
						</m.div>
					</div>
				</section>

				{/* Blog Introduction */}

				{/* Tags Section */}
				<section className="border-b border-zinc-100 bg-white sticky top-20 z-20 backdrop-blur-md bg-white/80 supports-[backdrop-filter]:bg-white/60">
					<div className="container mx-auto px-4 py-6 overflow-x-auto no-scrollbar">
						<div className="flex gap-3 items-center min-w-max mx-auto justify-center">
							<span className="font-syne text-sm font-bold uppercase tracking-wide text-zinc-400 mr-2">
								Topics:
							</span>
							<button
								type="button"
								onClick={() => setSelectedTag("")}
								className={`px-5 py-2 rounded-full text-sm font-bold font-syne uppercase tracking-wide transition-colors border ${
									selectedTag === ""
										? "bg-primary text-white border-primary"
										: "bg-white text-zinc-600 border-zinc-200 hover:border-zinc-300 hover:bg-zinc-50"
								}`}
							>
								All
							</button>
							{allTags.map((tag) => (
								<button
									type="button"
									key={tag}
									onClick={() => setSelectedTag(tag)}
									className={`px-5 py-2 rounded-full text-sm font-bold font-syne uppercase tracking-wide transition-colors border ${
										selectedTag === tag
											? "bg-primary text-white border-primary"
											: "bg-white text-zinc-600 border-zinc-200 hover:border-zinc-300 hover:bg-zinc-50"
									}`}
								>
									{tag}
								</button>
							))}
						</div>
					</div>
				</section>

				{/* Blog Grid */}
				<section className="py-12 bg-zinc-50/50">
					<div className="container mx-auto px-4">
						{filteredBlogs.length > 0 ? (
							<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
								{filteredBlogs.map((blog, index) => (
									<BlogCard
										key={blog.id}
										blog={blog}
										index={index}
										cardRefs={cardRefsMap}
										onCardClick={handleCardClick}
									/>
								))}
							</div>
						) : (
							<div className="text-center py-24 bg-white rounded-[2rem] border border-dashed border-zinc-300">
								<h3 className="font-syne text-2xl font-bold uppercase text-zinc-400 mb-2">
									No articles found
								</h3>
								<p className="font-inter text-zinc-500 mb-8">
									We couldn't find any articles matching your search criteria.
								</p>
								<Button
									variant="outline"
									className="rounded-full px-8 border-zinc-300 hover:bg-zinc-100 font-syne uppercase font-bold tracking-wide"
									onClick={() => {
										setSearchQuery("");
										setSelectedTag("");
									}}
								>
									Clear filters
								</Button>
							</div>
						)}
					</div>
				</section>

				{/* SEO Section (Bottom) */}
				<section className="py-12 bg-zinc-50 border-t border-zinc-200">
					<div className="container mx-auto px-4">
						<div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
							<div className="md:col-span-3">
								<h2 className="font-syne text-sm font-bold uppercase tracking-wide text-zinc-400">
									Growth Insights
								</h2>
							</div>
							<div className="md:col-span-9">
								<p className="font-inter text-zinc-500 text-sm leading-relaxed">
									Real talk about video marketing, audience building, and
									creator strategy — backed by data from thousands of campaigns.
								</p>
							</div>
						</div>
					</div>
				</section>

				{/* Newsletter Section */}
				<section className="py-24 bg-zinc-900 text-white relative overflow-hidden">
					{/* Abstract BG */}
					<div className="absolute inset-0 bg-[url(/noise.svg)] opacity-20 mix-blend-overlay pointer-events-none" />
					<div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-primary/10 to-transparent" />

					<div className="container mx-auto px-4 relative z-10">
						<div className="max-w-4xl mx-auto text-center">
							<h2 className="font-syne text-4xl md:text-5xl font-bold uppercase tracking-tight mb-6">
								Stay <span className="text-primary">Ahead</span>
							</h2>
							<p className="font-inter text-lg text-zinc-400 mb-10 max-w-2xl mx-auto">
								Subscribe to our newsletter to get the latest video marketing
								strategies, exclusive tips, and creator insights delivered
								straight to your inbox.
							</p>

							<BlogNewsletterForm source="blog index" />
							<p className="mt-6 text-xs text-zinc-500 font-inter">
								No spam. Unsubscribe anytime.
							</p>
						</div>
					</div>
				</section>
			</div>
		</LazyMotion>
	);
}
